import { useState, useCallback, useEffect } from "react";
import { sanitizeCategory } from "@/lib/categoryUtils";
import { safeStr } from "@/lib/utils";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { MoreHorizontal, Calendar, ChevronDown, ChevronUp, Plus, Pencil, Trash2, Share2 } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import EditGoalModal from "@/components/edit-goal-modal";
import ShareDialog from "@/components/share-dialog";
import HabitCard from "@/components/habit-card";
import type { HabitWithStreakState } from "@shared/schema";
import GoalProgressEntriesDialog from "@/components/goal-progress-entries-dialog";
import GoalTrackerXBranch from "@/components/goal-trackerx-branch";
import type { Goal, Milestone } from "@shared/schema";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { formatDateInput } from "@/lib/dateUtils";
import { useToast } from "@/hooks/use-toast";
import { SortableMilestoneItem } from "@/components/goal-detail/sortable-milestone-item";

const EMPTY_MILESTONES: Milestone[] = [];

interface GoalCardProps {
  goal: Goal;
  viewMode: "grid" | "list";
  expandAllSignal?: { expanded: boolean; seq: number } | null;
}
export default function GoalCard({ goal, viewMode, expandAllSignal }: GoalCardProps) {
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [progressEntriesOpen, setProgressEntriesOpen] = useState(false);
  const [milestonesExpanded, setMilestonesExpanded] = useState(false);
  const [habitsExpanded, setHabitsExpanded] = useState(false);
  const [trackerxExpanded, setTrackerxExpanded] = useState(false);

  // Respond to page-level Expand all / Collapse all
  useEffect(() => {
    if (!expandAllSignal) return;
    setHabitsExpanded(expandAllSignal.expanded);
    setTrackerxExpanded(expandAllSignal.expanded);
  }, [expandAllSignal]);
  const [localMilestones, setLocalMilestones] = useState<Milestone[]>([]);
  const [addMilestoneOpen, setAddMilestoneOpen] = useState(false);
  const [editMilestoneOpen, setEditMilestoneOpen] = useState(false);
  const [selectedMilestone, setSelectedMilestone] = useState<Milestone | null>(null);
  const [milestoneTitle, setMilestoneTitle] = useState("");
  const [milestoneDueDate, setMilestoneDueDate] = useState("");

  const handleCardClick = (e: React.MouseEvent) => {
    // Prevent navigation if clicking on interactive elements
    const target = e.target as HTMLElement;
    if (
      target.closest('button') ||
      target.closest('input') ||
      target.closest('[role="checkbox"]') ||
      target.closest('[data-radix-collection-item]')
    ) {
      return;
    }
    setLocation(`/goals/${goal.id}`);
  };

  const { data: milestones = EMPTY_MILESTONES, isLoading: milestonesLoading } = useQuery<Milestone[]>({
    queryKey: ["/api/goals", goal.id, "milestones"],
    enabled: milestonesExpanded,
  });

  // Habits linked to this goal — same habit system as the Habits page, just filtered by goal.
  const { data: goalHabits = [], isLoading: habitsLoading } = useQuery<HabitWithStreakState[]>({
    queryKey: ["/api/habits", goal.id],
    queryFn: async () => {
      const response = await fetch(`/api/habits?goalId=${encodeURIComponent(goal.id)}`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error("Failed to fetch habits");
      return response.json();
    },
    enabled: habitsExpanded,
  });

  useEffect(() => {
    if (!milestones) return;
    const sorted = [...milestones].sort((a, b) => a.displayOrder - b.displayOrder);
    setLocalMilestones((prev) => {
      if (
        prev.length === sorted.length &&
        prev.every((m, i) => m.id === sorted[i].id && m.achieved === sorted[i].achieved && m.displayOrder === sorted[i].displayOrder)
      ) {
        return prev;
      }
      return sorted;
    });
  }, [milestones]);

  const createMilestoneMutation = useMutation({
    mutationFn: (data: { title: string; dueDate?: string }) =>
      apiRequest("POST", `/api/goals/${goal.id}/milestones`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/goals", goal.id, "milestones"] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
      setAddMilestoneOpen(false);
      setMilestoneTitle("");
      setMilestoneDueDate("");
    },
    onError: () => {
      toast({ title: "Couldn't add milestone", description: "Please try again.", variant: "destructive" });
    },
  });

  const updateMilestoneMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Milestone> }) =>
      apiRequest("PATCH", `/api/milestones/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/goals", goal.id, "milestones"] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
    },
    onError: () => {
      setLocalMilestones(milestones);
      toast({ title: "Couldn't update milestone", description: "Your change was not saved. Please try again.", variant: "destructive" });
    },
  });

  const deleteMilestoneMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/milestones/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/goals", goal.id, "milestones"] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
    },
    onError: () => {
      toast({ title: "Couldn't delete milestone", description: "Please try again.", variant: "destructive" });
    },
  });

  const deleteGoalMutation = useMutation({
    mutationFn: async (id: string) => {
      console.log('🗑️ Delete Goal Request for goal:', id);
      try {
        const response = await apiRequest("DELETE", `/api/goals/${id}`);
        console.log('📡 Delete Goal Response:', { status: response.status, ok: response.ok });
        return response;
      } catch (error: any) {
        console.error('❌ Delete Goal Error:', {
          message: error?.message,
          name: error?.name,
          status: error?.status,
          stack: error?.stack,
          fullError: error
        });
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats/dashboard"] });
    },
    onError: (error: any) => {
      console.error('❌ Delete Goal Mutation Error:', error);
      toast({ title: "Couldn't delete goal", description: error?.message || "Please try again.", variant: "destructive" });
    },
  });

  const reorderMilestonesMutation = useMutation({
    mutationFn: (data: { goalId: string; milestoneOrders: { id: string; displayOrder: number }[] }) =>
      apiRequest("PATCH", "/api/milestones/reorder", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/goals", goal.id, "milestones"] });
    },
    onError: () => {
      toast({ title: "Couldn't reorder milestones", description: "Please try again.", variant: "destructive" });
    },
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = localMilestones.findIndex(item => item.id === active.id);
      const newIndex = localMilestones.findIndex(item => item.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const previousOrder = [...localMilestones];
        const newOrder = arrayMove(localMilestones, oldIndex, newIndex);
        setLocalMilestones(newOrder);

        const milestoneOrders = newOrder.map((milestone, index) => ({
          id: milestone.id,
          displayOrder: index + 1,
        }));

        reorderMilestonesMutation.mutate(
          { goalId: goal.id, milestoneOrders },
          {
            onError: () => {
              // Toast is shown by the mutation-level onError; just roll back.
              setLocalMilestones(previousOrder);
            }
          }
        );
      }
    }
  }, [localMilestones, reorderMilestonesMutation, goal.id]);

  const handleToggleMilestone = useCallback((id: string, achieved: boolean) => {
    setLocalMilestones(prev =>
      prev.map(m => m.id === id ? { ...m, achieved } : m)
    );
    updateMilestoneMutation.mutate({ id, data: { achieved } });
  }, [updateMilestoneMutation]);

  const handleEditMilestone = useCallback((milestone: Milestone) => {
    setSelectedMilestone(milestone);
    setMilestoneTitle(milestone.title);
    setMilestoneDueDate(milestone.dueDate ? formatDateInput(new Date(milestone.dueDate)) : "");
    setEditMilestoneOpen(true);
  }, []);

  const handleSaveEdit = useCallback(() => {
    if (selectedMilestone) {
      updateMilestoneMutation.mutate({
        id: selectedMilestone.id,
        data: {
          title: milestoneTitle,
          dueDate: milestoneDueDate ? new Date(milestoneDueDate) : undefined,
        },
      });
      setEditMilestoneOpen(false);
      setSelectedMilestone(null);
      setMilestoneTitle("");
      setMilestoneDueDate("");
    }
  }, [selectedMilestone, milestoneTitle, milestoneDueDate, updateMilestoneMutation]);

  const handleDeleteMilestone = useCallback((id: string) => {
    if (confirm("Are you sure you want to delete this milestone?")) {
      deleteMilestoneMutation.mutate(id);
    }
  }, [deleteMilestoneMutation]);

  const handleDeleteGoal = useCallback(() => {
    if (confirm("Are you sure you want to delete this goal? This will also delete all associated milestones.")) {
      deleteGoalMutation.mutate(goal.id);
    }
  }, [deleteGoalMutation, goal.id]);

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      "Health & Fitness": "bg-red-500",
      "Health": "bg-emerald-500",
      "Career": "bg-blue-500",
      "Financial": "bg-green-500",
      "Personal": "bg-purple-500",
      "Education": "bg-yellow-500",
    };
    return colors[category] || "bg-gray-500";
  };

  const getPriorityColor = (priority: string) => {
    const colors: Record<string, string> = {
      "high": "bg-red-500/10 text-red-600",
      "medium": "bg-yellow-500/10 text-yellow-600",
      "low": "bg-green-500/10 text-green-600",
    };
    return colors[priority] || "bg-gray-500/10 text-gray-600";
  };

  const formatDeadline = (deadline: Date | string | null) => {
    if (!deadline) return null;
    const date = new Date(deadline);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  const getProgressColor = (category: string) => {
    const colors: Record<string, string> = {
      "Health & Fitness": "text-red-500",
      "Health": "text-emerald-500",
      "Career": "text-blue-500",
      "Financial": "text-green-500",
      "Personal": "text-purple-500",
      "Education": "text-yellow-500",
    };
    return colors[category] || "text-gray-500";
  };

  const getProgressBarColor = (progress: number) => {
    if (progress <= 33) return "bg-red-500";
    if (progress <= 66) return "bg-yellow-500";
    return "bg-green-500";
  };

  const circumference = 2 * Math.PI * 15.9155;
  const strokeDasharray = `${(goal.progress / 100) * circumference}, ${circumference}`;

  const milestoneCount = milestones.length;
  const achievedCount = milestones.filter(m => m.achieved).length;

  if (viewMode === "list") {
    return (
      <Card
        className="goal-card cursor-pointer hover:shadow-lg transition-shadow"
        data-testid={`card-goal-${goal.id}`}
        onClick={handleCardClick}
      >
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 flex-1">
              <div className="relative w-12 h-12">
                <svg className="w-12 h-12 progress-circle" viewBox="0 0 36 36">
                  <path
                    className="text-muted stroke-current"
                    fill="none"
                    strokeWidth="3"
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                  <path
                    className={`${getProgressColor(goal.category)} stroke-current`}
                    fill="none"
                    strokeWidth="3"
                    strokeDasharray={strokeDasharray}
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xs font-bold text-foreground" data-testid={`text-progress-${goal.id}`}>
                    {Number(goal.progress) || 0}%
                  </span>
                </div>
              </div>

              <div className="flex-1 min-w-0">
                {sanitizeCategory(goal.category) && (
                  <div className="flex items-center space-x-2 mb-1">
                    <div className={`w-3 h-3 ${getCategoryColor(goal.category)} rounded-full`}></div>
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      {sanitizeCategory(goal.category)}
                    </span>
                  </div>
                )}
                <h3
                  className="text-lg font-semibold text-foreground mb-1 hover:underline cursor-pointer"
                  data-testid={`text-title-${goal.id}`}
                  onClick={(e) => { e.stopPropagation(); setEditModalOpen(true); }}
                >
                  {safeStr(goal.title)}
                </h3>
                <p className="text-muted-foreground text-sm line-clamp-1">{safeStr(goal.description)}</p>
              </div>

              <div className="flex items-center space-x-4">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    setHabitsExpanded(!habitsExpanded);
                  }}
                  data-testid={`button-expand-habits-${goal.id}`}
                >
                  {habitsExpanded ? (
                    <ChevronUp className="w-4 h-4 mr-1" />
                  ) : (
                    <ChevronDown className="w-4 h-4 mr-1" />
                  )}
                  <span className="text-sm font-medium">Habits</span>
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    setTrackerxExpanded(!trackerxExpanded);
                  }}
                  data-testid={`button-expand-trackerx-${goal.id}`}
                >
                  {trackerxExpanded ? (
                    <ChevronUp className="w-4 h-4 mr-1" />
                  ) : (
                    <ChevronDown className="w-4 h-4 mr-1" />
                  )}
                  <span className="text-sm font-medium">TrackerX</span>
                </Button>
                {goal.deadline && (
                  <div className="flex items-center space-x-1 text-muted-foreground">
                    <Calendar className="w-4 h-4" />
                    <span className="text-sm">{formatDeadline(goal.deadline)}</span>
                  </div>
                )}
                <Badge className={getPriorityColor(goal.priority)}>
                  {safeStr(goal.priority)} Priority
                </Badge>
              </div>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  data-testid={`button-menu-${goal.id}`}
                >
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onSelect={() => setShareDialogOpen(true)}
                  data-testid={`button-share-${goal.id}`}
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => setEditModalOpen(true)}
                  data-testid={`button-edit-${goal.id}`}
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit Goal
                </DropdownMenuItem>
                {goal.progressCalculationType === "custom" && (
                  <DropdownMenuItem
                    onSelect={() => setProgressEntriesOpen(true)}
                    data-testid={`button-manage-progress-${goal.id}`}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Manage Progress
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onSelect={handleDeleteGoal}
                  className="text-destructive focus:text-destructive"
                  data-testid={`button-delete-${goal.id}`}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Goal
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {habitsExpanded && (
            <div
              className="mt-4 space-y-3 cursor-default"
              onClick={(e) => e.stopPropagation()}
              data-testid={`section-goal-habits-${goal.id}`}
            >
              {habitsLoading ? (
                <div className="text-sm text-muted-foreground py-4 text-center">
                  Loading habits...
                </div>
              ) : goalHabits.length === 0 ? (
                <div className="text-sm text-muted-foreground py-4 text-center">
                  No habits linked to this goal yet
                </div>
              ) : (
                goalHabits.map((habit) => (
                  <HabitCard key={habit.id} habit={habit} viewMode="list" />
                ))
              )}
            </div>
          )}

          {trackerxExpanded && (
            <GoalTrackerXBranch goalId={goal.id} enabled={trackerxExpanded} />
          )}

          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted-foreground">Progress</span>
              <span className="text-sm font-bold text-foreground">{Number(goal.progress) || 0}%</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2.5">
              <div
                className={`h-2.5 rounded-full transition-all duration-300 ${getProgressBarColor(goal.progress)}`}
                style={{ width: `${Number(goal.progress) || 0}%` }}
                data-testid={`progress-bar-${goal.id}`}
              ></div>
            </div>
          </div>
        </CardContent>
        <EditGoalModal
          open={editModalOpen}
          onOpenChange={setEditModalOpen}
          goal={goal}
        />
        <ShareDialog
          isOpen={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          itemId={goal.id}
          itemType="goal"
          itemTitle={goal.title}
        />
        <GoalProgressEntriesDialog
          open={progressEntriesOpen}
          onOpenChange={setProgressEntriesOpen}
          goal={goal}
        />
      </Card>
    );
  }

  return (
    <>
      <Card
        className="goal-card cursor-pointer hover:shadow-lg transition-shadow"
        data-testid={`card-goal-${goal.id}`}
        onClick={handleCardClick}
      >
        <CardContent className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              {sanitizeCategory(goal.category) && (
                <>
                  <div className={`w-3 h-3 ${getCategoryColor(goal.category)} rounded-full`}></div>
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    {sanitizeCategory(goal.category)}
                  </span>
                </>
              )}
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  data-testid={`button-menu-${goal.id}`}
                >
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onSelect={() => setShareDialogOpen(true)}
                  data-testid={`button-share-${goal.id}`}
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => setEditModalOpen(true)}
                  data-testid={`button-edit-${goal.id}`}
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit Goal
                </DropdownMenuItem>
                {goal.progressCalculationType === "custom" && (
                  <DropdownMenuItem
                    onSelect={() => setProgressEntriesOpen(true)}
                    data-testid={`button-manage-progress-${goal.id}`}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Manage Progress
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onSelect={handleDeleteGoal}
                  className="text-destructive focus:text-destructive"
                  data-testid={`button-delete-${goal.id}`}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Delete Goal
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <h3
            className="text-lg font-semibold text-foreground mb-2 hover:underline cursor-pointer"
            data-testid={`text-title-${goal.id}`}
            onClick={(e) => { e.stopPropagation(); setEditModalOpen(true); }}
          >
            {safeStr(goal.title)}
          </h3>
          <p className="text-muted-foreground text-sm mb-4 line-clamp-2">{safeStr(goal.description)}</p>

          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <div className="relative w-12 h-12">
                <svg className="w-12 h-12 progress-circle" viewBox="0 0 36 36">
                  <path
                    className="text-muted stroke-current"
                    fill="none"
                    strokeWidth="3"
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                  <path
                    className={`${getProgressColor(goal.category)} stroke-current`}
                    fill="none"
                    strokeWidth="3"
                    strokeDasharray={strokeDasharray}
                    d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-xs font-bold text-foreground" data-testid={`text-progress-${goal.id}`}>
                    {Number(goal.progress) || 0}%
                  </span>
                </div>
              </div>
              <div>
                <p className="text-sm font-medium text-foreground">
                  {goal.currentValue && goal.targetValue ? (
                    `${goal.currentValue} of ${goal.targetValue}`
                  ) : (
                    `${Number(goal.progress) || 0}% complete`
                  )}
                </p>
                <p className="text-xs text-muted-foreground">
                  {goal.deadline ? `Due ${formatDeadline(goal.deadline)}` : "No deadline set"}
                </p>
              </div>
            </div>
          </div>

          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setMilestonesExpanded(!milestonesExpanded)}
                  className="h-8 px-2"
                  data-testid={`button-expand-milestones-${goal.id}`}
                >
                  {milestonesExpanded ? (
                    <ChevronUp className="w-4 h-4 mr-1" />
                  ) : (
                    <ChevronDown className="w-4 h-4 mr-1" />
                  )}
                  <span className="text-sm font-medium">Milestones</span>
                </Button>
                {milestoneCount > 0 && (
                  <Badge variant="secondary" className="text-xs" data-testid={`badge-milestone-count-${goal.id}`}>
                    {achievedCount}/{milestoneCount}
                  </Badge>
                )}
              </div>
              {milestonesExpanded && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setAddMilestoneOpen(true)}
                  className="h-8"
                  data-testid={`button-add-milestone-${goal.id}`}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              )}
            </div>

            {milestonesExpanded && (
              <div className="space-y-2">
                {milestonesLoading ? (
                  <div className="text-sm text-muted-foreground py-4 text-center">
                    Loading milestones...
                  </div>
                ) : localMilestones.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-4 text-center">
                    No milestones yet. Click "Add" to create one.
                  </div>
                ) : (
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                  >
                    <SortableContext
                      items={localMilestones.map(m => m.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      {localMilestones.map((milestone) => (
                        <SortableMilestoneItem
                          key={milestone.id}
                          milestone={milestone}
                          goalId={goal.id}
                          onToggle={handleToggleMilestone}
                          onEdit={handleEditMilestone}
                          onDelete={handleDeleteMilestone}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                )}
              </div>
            )}
          </div>

          {habitsExpanded && (
            <div
              className="mt-4 space-y-3 cursor-default"
              onClick={(e) => e.stopPropagation()}
              data-testid={`section-goal-habits-${goal.id}`}
            >
              {habitsLoading ? (
                <div className="text-sm text-muted-foreground py-4 text-center">
                  Loading habits...
                </div>
              ) : goalHabits.length === 0 ? (
                <div className="text-sm text-muted-foreground py-4 text-center">
                  No habits linked to this goal yet
                </div>
              ) : (
                goalHabits.map((habit) => (
                  <HabitCard key={habit.id} habit={habit} viewMode="list" />
                ))
              )}
            </div>
          )}

          {trackerxExpanded && (
            <GoalTrackerXBranch goalId={goal.id} enabled={trackerxExpanded} />
          )}

          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-muted-foreground">Progress</span>
              <span className="text-sm font-bold text-foreground">{Number(goal.progress) || 0}%</span>
            </div>
            <div className="w-full bg-muted rounded-full h-2.5">
              <div
                className={`h-2.5 rounded-full transition-all duration-300 ${getProgressBarColor(goal.progress)}`}
                style={{ width: `${Number(goal.progress) || 0}%` }}
                data-testid={`progress-bar-${goal.id}`}
              ></div>
            </div>
          </div>
        </CardContent>
        <EditGoalModal
          open={editModalOpen}
          onOpenChange={setEditModalOpen}
          goal={goal}
        />
        <ShareDialog
          isOpen={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          itemId={goal.id}
          itemType="goal"
          itemTitle={goal.title}
        />
        <GoalProgressEntriesDialog
          open={progressEntriesOpen}
          onOpenChange={setProgressEntriesOpen}
          goal={goal}
        />
      </Card>

      {/* Add Milestone Dialog */}
      <Dialog open={addMilestoneOpen} onOpenChange={setAddMilestoneOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Milestone</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="milestone-title">Title</Label>
              <Input
                id="milestone-title"
                value={milestoneTitle}
                onChange={(e) => setMilestoneTitle(e.target.value)}
                placeholder="Enter milestone title"
                data-testid="input-milestone-title"
              />
            </div>
            <div>
              <Label htmlFor="milestone-due-date">Due Date (optional)</Label>
              <Input
                id="milestone-due-date"
                type="date"
                value={milestoneDueDate}
                onChange={(e) => setMilestoneDueDate(e.target.value)}
                data-testid="input-milestone-due-date"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddMilestoneOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => createMilestoneMutation.mutate({
                title: milestoneTitle,
                dueDate: milestoneDueDate || undefined
              })}
              disabled={!milestoneTitle.trim() || createMilestoneMutation.isPending}
              data-testid="button-create-milestone"
            >
              {createMilestoneMutation.isPending ? "Creating..." : "Create Milestone"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Milestone Dialog */}
      <Dialog open={editMilestoneOpen} onOpenChange={setEditMilestoneOpen}>
        <DialogContent data-testid="dialog-content-edit-milestone-goalcard">
          <DialogHeader>
            <DialogTitle>Edit Milestone</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-milestone-title">Title</Label>
              <Input
                id="edit-milestone-title"
                value={milestoneTitle}
                onChange={(e) => setMilestoneTitle(e.target.value)}
                placeholder="Enter milestone title"
                data-testid="input-edit-milestone-title"
              />
            </div>
            <div>
              <Label htmlFor="edit-milestone-due-date">Due Date (optional)</Label>
              <Input
                id="edit-milestone-due-date"
                type="date"
                value={milestoneDueDate}
                onChange={(e) => setMilestoneDueDate(e.target.value)}
                data-testid="input-edit-milestone-due-date"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditMilestoneOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={!milestoneTitle.trim() || updateMilestoneMutation.isPending}
              data-testid="button-save-milestone"
            >
              {updateMilestoneMutation.isPending ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
